import { useScrollReveal } from '../../../../hooks/useScrollReveal';

const features = [
  {
    icon: 'ri-heart-2-fill',
    title: 'Full Wedding Party Transport',
    desc: 'Coordinated transportation for the bride, groom, bridesmaids, groomsmen, and guests — every vehicle timed and positioned perfectly for your schedule.',
  },
  {
    icon: 'ri-calendar-check-fill',
    title: 'Day-of Schedule Coordination',
    desc: 'We work from your wedding day timeline to ensure every transfer — from getting ready to venue to reception — runs on time without any last-minute stress.',
  },
  {
    icon: 'ri-car-2-fill',
    title: 'Luxury Vehicles for Every Occasion',
    desc: 'Pristine, elegant luxury vehicles that match the tone of your special day. Clean interiors, professional presentation, and the style your wedding deserves.',
  },
  {
    icon: 'ri-shield-check-fill',
    title: 'Professional, Discreet Chauffeurs',
    desc: 'Our wedding drivers are experienced, professionally dressed, and trained to remain calm and helpful throughout your big day — no matter what arises.',
  },
  {
    icon: 'ri-map-2-line',
    title: 'Full Route Planning Included',
    desc: 'We pre-plan every route between venue locations, accounts for traffic, parking, and timing so there are zero delays on one of the most important days of your life.',
  },
  {
    icon: 'ri-customer-service-2-fill',
    title: '24/7 Support for Your Event',
    desc: 'From the planning stage to the last drop-off after the reception, our team is reachable day and night to handle any changes, requests, or adjustments.',
  },
];

export default function WeddingServiceBreakdown() {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="section-reveal">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="w-full h-[420px] overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/d40c401e49d149d0d96cacc1c617fe83.jpg"
                  alt="Wedding transportation services Charleston SC"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-2 border-gold/30 pointer-events-none"></div>
            </div>
            {/* Text */}
            <div className="order-1 lg:order-2">
              <span className="text-gold text-xs tracking-widest uppercase font-semibold">Wedding Transportation Specialists</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-neutral-900 mt-3 mb-6 leading-tight">
                Your Wedding Day<br />
                <span className="text-gold">Deserves Perfection</span>
              </h2>
              <p className="text-neutral-600 leading-relaxed text-sm mb-4">
                Your wedding day should feel calm, beautiful, and perfectly coordinated. Tec&apos;s Limo Service handles all transportation logistics so you and your party arrive exactly when and where you need to be — elegantly and effortlessly.
              </p>
              <p className="text-neutral-500 leading-relaxed text-sm">
                From the bridal suite to the ceremony, from the ceremony to the reception and beyond — every transfer is planned, timed, and executed with the same care and precision that your wedding deserves. No stress, no surprises.
              </p>
              <div className="mt-8 flex gap-4">
                <a href="#contact" className="bg-neutral-900 hover:bg-gold transition-colors text-white hover:text-black font-bold px-8 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer">
                  Book Wedding Transport
                </a>
                <a href="tel:+18436422440" className="flex items-center gap-2 border-2 border-neutral-200 hover:border-gold text-neutral-700 hover:text-gold transition-colors font-semibold px-8 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-sm"></i></div>
                  Call Now
                </a>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feat) => (
              <div key={feat.title} className="group p-6 border border-neutral-100 hover:border-gold/30 hover:bg-neutral-50 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center bg-gold/10 group-hover:bg-gold/20 transition-colors mb-4">
                  <i className={`${feat.icon} text-gold text-xl`}></i>
                </div>
                <h4 className="font-semibold text-neutral-900 text-sm mb-2 group-hover:text-gold transition-colors">{feat.title}</h4>
                <p className="text-neutral-500 text-xs leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
